/**
 * Which BUs can generate a signature.
 *
 * The signature became step 2 of the BU setup, so a BU without it configured cannot generate
 * one — the BFF answers `POST /v1/email/send-signature` with 422. The screen shows that as a
 * padlock on the chip rather than letting the person try and fail.
 *
 * @module Pages/Signatures
 */

import type { SignatureStatus } from '../Components/SignatureList/Signatures.types';
import type { Tenant } from '@/Pages/BUs/BUManager/Components/BUPlatformList/BUPlatformList.types';
import type { Status } from '@/Shared/components/StatusTag/StatusTag';

/**
 * Whether the BU has the signature step done.
 *
 * Prefers `setupSteps.signature`, which the BFF computes; falls back to the presence of a subject
 * or a config for a deployment that predates it.
 */
export function canTenantGenerateSignatures(tenant: Tenant): boolean {
  if (tenant.setupSteps) return tenant.setupSteps.signature;

  return Boolean(tenant.signatureSubject) || Boolean(tenant.signatureConfig);
}

/** Active BUs, split by whether they can generate signatures. */
export function splitTenantsBySignatureAccess(tenants: Tenant[]): {
  enabled: Tenant[];
  blocked: Tenant[];
} {
  const active = tenants.filter(tenant => tenant.isActive);

  return {
    enabled: active.filter(canTenantGenerateSignatures),
    blocked: active.filter(tenant => !canTenantGenerateSignatures(tenant)),
  };
}

const STATUS_PRESENTATION: Record<SignatureStatus, { status: Status; label: string }> = {
  DRAFT: { status: 'neutral', label: 'Rascunho' },
  GENERATING: { status: 'pending', label: 'Gerando' },
  DELIVERED: { status: 'success', label: 'Entregue' },
};

/** Pill color and label for a signature's status. */
export function getSignatureStatusPresentation(status: SignatureStatus): {
  status: Status;
  label: string;
} {
  return STATUS_PRESENTATION[status] ?? STATUS_PRESENTATION.GENERATING;
}
